// ClientHodnoceniSection — hodnocení spolupráce klienta (20.7.2026).
// Nahrazuje Google Form „Hodnocení komplexního koučinku": dotazník se vyplňuje
// přímo v detailu klienta (trenér/CSM při prodloužení), odpovědi jdou do PG
// a jsou vidět real-time. Staré odpovědi z formu jsou importované (zdroj pill).
//
// Použití: <ClientHodnoceniSection klientId={client.notion_id} klientName={client.klient} trener={jmenoTrenera} readOnly={bool} />

import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Star, Plus, X, ChevronDown, ChevronRight, Trash2, MessageSquareHeart } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { useUzkaObrazovka } from "../lib/obrazovka.js";

// Struktura dotazníku — stejné otázky jako původní Google Form.
// typ: skala5 (1–5), skala10 (1–10), anone (Ano/Ne), text
export const OTAZKY = [
  { k: "spokojenost_treninky", q: "Jak jste spokojeni s průběhem tréninků?", typ: "skala5", sekce: "Tréninky" },
  { k: "zabavnost", q: "Jsou pro vás tréninky zábavné a rozmanité?", typ: "text", sekce: "Tréninky" },
  { k: "obtiznost", q: "Odpovídá obtížnost tréninků vašim schopnostem a cílům?", typ: "skala5", sekce: "Tréninky" },
  { k: "zmeny", q: "Vidíte na sobě změny (fyzické nebo mentální) od začátku spolupráce?", typ: "text", sekce: "Tréninky" },
  { k: "prinos", q: "Jaký je podle vás největší přínos tréninků?", typ: "text", sekce: "Tréninky" },
  { k: "pristup_trenera", q: "Vyhovuje vám styl a přístup vašeho trenéra?", typ: "text", sekce: "Trenér" },
  { k: "motivace", q: "Cítíte se během tréninků dostatečně motivovaní a podporovaní?", typ: "anone", sekce: "Trenér" },
  { k: "pozornost", q: "Je trenér během tréninků dostatečně pozorný?", typ: "anone", sekce: "Trenér" },
  { k: "dochvilnost", q: "Je trenér dochvilný?", typ: "anone", sekce: "Trenér" },
  { k: "instrukce", q: "Dává vám trenér jasné a srozumitelné instrukce?", typ: "anone", sekce: "Trenér" },
  { k: "komunikace", q: "Je komunikace s trenérem mimo tréninky v pořádku?", typ: "anone", sekce: "Trenér" },
  { k: "prostredi", q: "Jak byste ohodnotili prostředí, kde tréninky probíhají?", typ: "skala5", sekce: "Prostředí" },
  { k: "atmosfera", q: "Je atmosféra při tréninku příjemná a přátelská?", typ: "skala5", sekce: "Prostředí" },
  { k: "cena_hodnota", q: "Odpovídá cena hodnotě, kterou jste získali? (1–10)", typ: "skala10", sekce: "Celkově" },
  { k: "doporuceni", q: "Doporučili byste naše služby přátelům nebo rodině?", typ: "anone", sekce: "Celkově" },
  { k: "celkove", q: "Jak byste celkově ohodnotili spolupráci s naším týmem?", typ: "skala5", sekce: "Celkově" },
  { k: "nutrice", q: "Využila jste nutriční konzultaci?", typ: "anone", sekce: "Výživa" },
  { k: "nutrice_spokojenost", q: "Jak jste byla spokojená s nutriční konzultací?", typ: "text", sekce: "Výživa" },
  { k: "co_vylepsit", q: "Chybí vám něco, čím bychom mohli tréninky vylepšit?", typ: "text", sekce: "Zpětná vazba" },
  { k: "dalsi_sluzby", q: "Jaké další služby nebo aktivity byste uvítala?", typ: "text", sekce: "Zpětná vazba" },
  { k: "navrhy", q: "Návrhy na zlepšení komunikace nebo organizace?", typ: "text", sekce: "Zpětná vazba" },
  { k: "pripominky", q: "Máte nějaké připomínky?", typ: "text", sekce: "Zpětná vazba" },
  { k: "jak_dlouho", q: "Jak dlouho s námi trénujete?", typ: "vyber", moznosti: ["Méně než 3 měsíce", "Méně než 6 měsíců", "Méně než rok", "Více než rok"], sekce: "Zpětná vazba" },
];

function fmtCz(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}` : iso;
}

export function Hvezdicky({ n, max = 5, velke = false }) {
  const v = parseFloat(n);
  if (Number.isNaN(v)) return null;
  return (
    <span className="inline-flex items-center gap-0.5">
      {[...Array(max)].map((_, i) => (
        <Star key={i} className={`${velke ? "w-5 h-5" : "w-3.5 h-3.5"} ${i < Math.round(v) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
      ))}
    </span>
  );
}

// Barevná pilulka pro odpověď dle typu (skála / ano-ne).
export function OdpovedPill({ otazka, hodnota, velke = false }) {
  const s = String(hodnota).trim();
  if (otazka?.typ === "skala5") return <Hvezdicky n={s} velke={velke} />;
  const pilulka = velke ? "text-[13px] font-bold px-3 py-1 rounded-full" : "text-xs font-bold px-2 py-0.5 rounded-full";
  if (otazka?.typ === "skala10") {
    const v = parseFloat(s);
    const tone = v >= 8 ? "bg-emerald-50 text-emerald-700" : v >= 5 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700";
    return <span className={`${pilulka} tabular-nums ${tone}`}>{s}{velke ? " z 10" : "/10"}</span>;
  }
  const lower = s.toLowerCase();
  if (lower === "ano") return <span className={`${pilulka} bg-emerald-50 text-emerald-700`}>Ano</span>;
  if (lower === "ne") return <span className={`${pilulka} bg-rose-50 text-rose-700`}>Ne</span>;
  return <span className={velke ? "text-[14px] leading-snug text-slate-700" : "text-sm text-slate-700"}>{s}</span>;
}

// 9.9.2026 — odpovědi se v detailu seskupí po sekcích (Tréninky, Trenér, …) a
// u každé sekce se spočítá krátký souhrn do pruhu: průměr hvězdiček, kolik Ano
// nebo hodnota desetibodové škály. Prázdné otázky se přeskakují.
function souhrnSekce(polozky, odp) {
  const hvezdy = polozky.filter((o) => o.typ === "skala5").map((o) => parseFloat(odp[o.k])).filter((v) => !Number.isNaN(v));
  if (hvezdy.length) {
    const prumer = hvezdy.reduce((a, b) => a + b, 0) / hvezdy.length;
    return `${prumer.toFixed(1).replace(/\.0$/, "").replace(".", ",")}/5`;
  }
  const anone = polozky.filter((o) => o.typ === "anone");
  if (anone.length) {
    const kolik = anone.filter((o) => String(odp[o.k]).trim().toLowerCase() === "ano").length;
    return `${kolik} z ${anone.length} Ano`;
  }
  const desitka = polozky.find((o) => o.typ === "skala10");
  if (desitka) return `${odp[desitka.k]}/10`;
  return "";
}
function sekceOdpovedi(odp) {
  const poradi = [];
  const dleSekce = {};
  for (const o of OTAZKY) {
    const v = odp?.[o.k];
    if (v == null || String(v).trim() === "") continue;
    if (!dleSekce[o.sekce]) { dleSekce[o.sekce] = { nazev: o.sekce, polozky: [] }; poradi.push(dleSekce[o.sekce]); }
    dleSekce[o.sekce].polozky.push(o);
  }
  for (const sk of poradi) sk.souhrn = souhrnSekce(sk.polozky, odp);
  return poradi;
}

// Jedno hodnocení — řádek s rozbalením všech odpovědí.
function HodnoceniRadek({ h, onSmazat, jeOwner }) {
  const [open, setOpen] = useState(false);
  const celkove = parseFloat(h.odpovedi?.celkove);
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      {/* 9.9.2026 — datum a hvězdičky velké na prvním řádku, trenér a zdroj jako odznaky
          pod tím. Dřív se to všechno tlačilo do jednoho řádku a jméno trenéra se urvalo. */}
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full px-3 py-2.5 text-left transition-colors hover:bg-slate-50">
        <span className="flex items-center gap-2">
          <span className="text-[15px] font-bold text-slate-800">{fmtCz(h.datum)}</span>
          {!Number.isNaN(celkove) && <span className="ml-auto"><Hvezdicky n={celkove} velke /></span>}
          <ChevronRight className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-90" : ""} ${Number.isNaN(celkove) ? "ml-auto" : ""}`} />
        </span>
        {(h.trener || h.zdroj === "import") && (
          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {h.trener && <span className="rounded-full bg-plum-50 px-2 py-0.5 text-[11.5px] font-bold text-plum-700 demo-sensitive">trenér {h.trener}</span>}
            {h.zdroj === "import" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11.5px] font-bold text-slate-500">Google Form</span>}
          </span>
        )}
      </button>
      {open && (
        <div className="border-t border-slate-100 p-3">
          {sekceOdpovedi(h.odpovedi).map((sk) => (
            <div key={sk.nazev} className="mt-3 first:mt-0">
              <div className="flex items-center justify-between gap-2 rounded-xl bg-brand-100 px-3 py-1.5">
                <span className="text-[14px] font-extrabold uppercase tracking-wide text-brand-800">{sk.nazev}</span>
                {sk.souhrn ? <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[11.5px] font-bold text-brand-800">{sk.souhrn}</span> : null}
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {sk.polozky.map((o) => (
                  o.typ === "anone" ? (
                    <div key={o.k} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <span className="min-w-0 flex-1 text-[13.5px] font-bold leading-snug text-slate-800">{o.q}</span>
                      <span className="shrink-0"><OdpovedPill otazka={o} hodnota={h.odpovedi[o.k]} /></span>
                    </div>
                  ) : (
                    <div key={o.k} className={`rounded-xl border border-slate-200 bg-white px-3 py-2 ${o.typ === "text" ? "sm:col-span-2" : ""}`}>
                      <div className="text-[13.5px] font-bold leading-snug text-slate-800">{o.q}</div>
                      <div className="mt-1.5"><OdpovedPill otazka={o} hodnota={h.odpovedi[o.k]} velke /></div>
                    </div>
                  )
                ))}
              </div>
            </div>
          ))}
          {jeOwner && (
            <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-2">
              <span className="text-[11.5px] text-slate-500">vyplněno {fmtCz(h.datum)}</span>
              <button type="button" onClick={() => onSmazat(h.id)} title="Smazat hodnocení" aria-label="Smazat hodnocení"
                className="ml-auto flex h-11 w-11 items-center justify-center rounded-xl border border-rose-200 text-rose-500 hover:bg-rose-50">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Modal s dotazníkem — stejné otázky jako Google Form, seskupené po sekcích.
function HodnoceniFormModal({ klientName, trener, onClose, onSubmit }) {
  const uzka = useUzkaObrazovka();
  const [odp, setOdp] = useState({});
  const [posilam, setPosilam] = useState(false);
  const set = (k) => (v) => setOdp((o) => ({ ...o, [k]: v }));
  const sekce = [...new Set(OTAZKY.map((o) => o.sekce))];
  const jeVyplneno = (k) => String(odp[k] ?? "").trim() !== "";
  // Vsechny otazky jsou povinne. Vyjimka: spokojenost s nutrici je povinna jen kdyz
  // klientka nutricni konzultaci vyuzila (odpovedela "Ano") — jinak by to nedavalo smysl.
  const jeVyzadovana = (o) => (o.k === "nutrice_spokojenost" ? odp["nutrice"] === "Ano" : true);
  const chybejici = OTAZKY.filter((o) => jeVyzadovana(o) && !jeVyplneno(o.k));
  const hotovo = OTAZKY.filter((o) => jeVyplneno(o.k)).length;
  const pct = Math.round((100 * hotovo) / OTAZKY.length);
  const czOtazek = (n) => (n === 1 ? "otázka" : n >= 2 && n <= 4 ? "otázky" : "otázek");
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-brand-dark/50 backdrop-blur-[2px] sm:p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="flex w-full max-w-4xl flex-col bg-white shadow-2xl sm:my-6 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Hlavička + ukazatel postupu (9.9.2026) */}
        <div className="sticky top-0 z-10 bg-white">
          <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600 sm:h-10 sm:w-10">
              <MessageSquareHeart className="h-4 w-4 sm:h-5 sm:w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-bold text-slate-900">Hodnocení spolupráce</div>
              <div className="truncate text-xs text-slate-500 demo-sensitive">{klientName}{uzka ? "" : ", vyplň s klientkou (např. při prodloužení)"}</div>
            </div>
            {!uzka && <span className="shrink-0 rounded-full bg-brand-100 px-2.5 py-1 text-[11.5px] font-bold text-brand-800">Vyplněno {hotovo} z {OTAZKY.length}</span>}
            <button onClick={onClose} aria-label="Zavřít" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2 sm:px-5">
            <span className="text-[11.5px] font-bold text-slate-500">Vyplněno {hotovo} z {OTAZKY.length}</span>
            <span className="ml-auto block h-1.5 w-24 overflow-hidden rounded-full bg-slate-200">
              <span className="block h-full rounded-full bg-brand transition-all" style={{ width: `${pct}%` }} />
            </span>
          </div>
        </div>

        {/* Otázky po sekcích. Krátké otázky jsou na počítači po dvou vedle sebe,
            textové a škály přes celou šířku. */}
        <div className="flex-1 space-y-4 bg-[#FBF9FC] p-3 sm:p-4">
          {sekce.map((sk) => {
            const vsechny = OTAZKY.filter((o) => o.sekce === sk);
            const hotovoSekce = vsechny.filter((o) => jeVyplneno(o.k)).length;
            return (
              <div key={sk}>
                <div className="flex items-center justify-between gap-2 rounded-xl bg-brand-100 px-3 py-1.5">
                  <span className="text-[14px] font-extrabold uppercase tracking-wide text-brand-800">{sk}</span>
                  <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[11.5px] font-bold text-brand-800">{hotovoSekce} z {vsechny.length}</span>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {vsechny.map((o) => {
                    const prazdna = !jeVyplneno(o.k);
                    const siroka = o.typ === "text" || o.typ === "vyber" || o.typ === "skala10";
                    return (
                      <div key={o.k} className={`rounded-xl bg-white p-3 ${prazdna && o.typ === "text" ? "border-2 border-dashed border-slate-300" : "border border-slate-200"} ${siroka ? "sm:col-span-2" : ""}`}>
                        <div className="text-[13.5px] font-bold leading-snug text-slate-800">
                          {o.q}{jeVyzadovana(o) && <span className="ml-0.5 text-rose-500">*</span>}
                        </div>

                        {o.typ === "skala5" && (
                          <div className="mt-2 flex items-center justify-between gap-2">
                            <span className="flex items-center">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <button key={n} type="button" onClick={() => set(o.k)(n)} className="p-1" title={`${n} z 5`}>
                                  <Star className={`h-7 w-7 transition-colors ${n <= (parseInt(odp[o.k], 10) || 0) ? "text-amber-400 fill-amber-400" : "text-slate-200 hover:text-amber-200"}`} />
                                </button>
                              ))}
                            </span>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11.5px] font-bold ${odp[o.k] ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-400"}`}>
                              {odp[o.k] ? `${odp[o.k]} z 5` : "nevyplněno"}
                            </span>
                          </div>
                        )}

                        {o.typ === "skala10" && (
                          <div className="mt-2 grid grid-cols-5 gap-1.5 sm:grid-cols-10">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                              <button key={n} type="button" onClick={() => set(o.k)(n)}
                                className={`flex min-h-[44px] items-center justify-center rounded-xl border-2 text-sm font-extrabold transition-colors ${n === parseInt(odp[o.k], 10) ? "border-brand-deep bg-brand-deep text-white" : "border-slate-200 bg-white text-slate-500 hover:border-brand-300"}`}>
                                {n}
                              </button>
                            ))}
                          </div>
                        )}

                        {o.typ === "anone" && (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            {["Ano", "Ne"].map((v) => (
                              <button key={v} type="button" onClick={() => set(o.k)(v)}
                                className={`flex min-h-[44px] items-center justify-center rounded-xl border-2 text-sm font-bold transition-colors ${odp[o.k] === v ? (v === "Ano" ? "border-emerald-600 bg-emerald-600 text-white" : "border-rose-500 bg-rose-500 text-white") : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                                {v}
                              </button>
                            ))}
                          </div>
                        )}

                        {o.typ === "vyber" && (
                          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {o.moznosti.map((v) => (
                              <button key={v} type="button" onClick={() => set(o.k)(v)}
                                className={`flex min-h-[44px] items-center justify-center rounded-xl border-2 px-2 text-center text-sm font-bold transition-colors ${odp[o.k] === v ? "border-brand-deep bg-brand-deep text-white" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                                {v}
                              </button>
                            ))}
                          </div>
                        )}

                        {o.typ === "text" && (
                          <textarea rows={3}
                            className="field mt-2 w-full resize-y"
                            placeholder="Napiš odpověď klientky…"
                            value={odp[o.k] || ""} onChange={(e) => set(o.k)(e.target.value)} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Patička je pořád vidět, ať se nemusí rolovat až dolů. 14. 9. 2026 (fáze 6): opírá se o klávesnici. */}
        <div className="safe-bottom lepive-nad-klavesnici sticky bottom-0 flex items-center gap-2 border-t border-slate-200 bg-white px-3 py-2.5 sm:px-5 sm:py-3">
          {chybejici.length > 0
            ? <span className="text-[11.5px] font-bold text-rose-500 sm:text-xs">Zbývá {chybejici.length} {czOtazek(chybejici.length)}</span>
            : <span className="text-[11.5px] font-bold text-emerald-600 sm:text-xs">Vše vyplněno</span>}
          <button onClick={onClose} className="ml-auto hidden rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 sm:inline-flex">Zrušit</button>
          <button
            disabled={posilam || chybejici.length > 0}
            onClick={async () => { setPosilam(true); await onSubmit(odp, trener); setPosilam(false); }}
            className="btn btn-primary ml-auto">
            {posilam ? "Ukládám…" : "Uložit hodnocení"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ClientHodnoceniSection({ klientId, klientName, trener = "", readOnly = false, bezRamce = false, onCount, openTrigger = 0 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [open, setOpen] = useState(false);
  const uzka = useUzkaObrazovka();
  const jeOwner = String(getCurrentUser()?.role || "").toLowerCase() === "owner";

  const load = useCallback(async () => {
    if (!klientId) return;
    try {
      const r = await apiFetch(`/api/klient-hodnoceni?klientId=${encodeURIComponent(klientId)}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Načtení selhalo");
      setData(j);
    } catch (e) { setErr(e.message); }
  }, [klientId]);
  useEffect(() => { load(); }, [load]);

  // Průměr do odznaku v hlavičce sekce a otevření dotazníku tlačítkem z hlavičky
  // detailu klienta (9.9.2026).
  useEffect(() => {
    if (typeof onCount !== "function" || !data) return;
    const pocet = (data.hodnoceni || []).length;
    if (data.prumer != null) onCount(String(data.prumer).replace(".", ","));
    else onCount(pocet ? `${pocet}×` : 0);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (openTrigger > 0 && !readOnly) setShowForm(true); }, [openTrigger, readOnly]);

  const submit = async (odpovedi, trenerJmeno) => {
    try {
      const r = await apiFetch("/api/klient-hodnoceni", {
        method: "POST",
        body: JSON.stringify({ klientId, klientName, trener: trenerJmeno || trener, odpovedi }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      setShowForm(false);
      setOpen(true);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const smazat = async (id) => {
    try {
      await apiFetch("/api/klient-hodnoceni", { method: "POST", body: JSON.stringify({ action: "smazat", id }) });
      await load();
    } catch { /* načte se znovu */ }
  };

  const hodnoceni = data?.hodnoceni || [];
  if (!klientId) return null;

  return (
    <div className={bezRamce ? "" : "bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"} id="hodnoceni">
      {!bezRamce && (
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-slate-50/60 transition-colors">
        <span className="w-8 h-8 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0"><MessageSquareHeart className="w-4 h-4" /></span>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-bold text-slate-800">{uzka ? "Hodnocení" : "Hodnocení spolupráce"}</span>
          {!uzka && <div className="text-xs text-slate-400">dotazník s klientkou, dřív Google Form, teď přímo tady</div>}
        </div>
        {/* 9.9.2026 — na telefonu je z průměru, hvězdiček a počtu jeden odznak, ať se hlavička vejde. */}
        {uzka ? (
          <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[11.5px] font-bold text-amber-700 tabular-nums">
            {data?.prumer != null ? `${data.prumer}/5, ` : ""}{hodnoceni.length}×
          </span>
        ) : (
          <>
            {data?.prumer != null && (
              <span className="flex items-center gap-1.5 shrink-0">
                <Hvezdicky n={data.prumer} />
                <span className="text-xs font-bold text-slate-500 tabular-nums">{data.prumer}/5</span>
              </span>
            )}
            <span className="text-xs font-bold text-slate-400 tabular-nums shrink-0">({hodnoceni.length})</span>
          </>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      )}
      {(open || bezRamce) && (
        <div className={bezRamce ? "space-y-2" : "px-4 pb-4 space-y-2 border-t border-slate-100 pt-3"}>
          {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{err}</div>}
          {hodnoceni.length === 0
            ? <div className="text-sm text-slate-400">Zatím žádné hodnocení.</div>
            : hodnoceni.map((h) => <HodnoceniRadek key={h.id} h={h} onSmazat={smazat} jeOwner={jeOwner} />)}
          {!readOnly && (
            <button type="button" onClick={() => setShowForm(true)}
              className={`flex w-full items-center justify-center gap-2 rounded-xl transition-colors ${uzka ? "min-h-[44px] bg-brand text-sm font-bold text-white" : "border border-brand-200 bg-brand-50 px-3 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-100"}`}>
              <Plus className="h-4 w-4" /> {uzka ? "Nové hodnocení" : "Nové hodnocení (vyplň s klientkou)"}
            </button>
          )}
        </div>
      )}
      {showForm && <HodnoceniFormModal klientName={klientName} trener={trener} onClose={() => setShowForm(false)} onSubmit={submit} />}
    </div>
  );
}
